import React, { useState } from 'react';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';

interface Resource {
  id: number;
  title: string;
  description: string | null;
  filename: string;
  contentType: string | null;
  size: number | null;
  audience: 'all' | 'accepted';
}

export default function Resources() {
  const { data, loading, error } = useApi(() => API.resources());

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error) return <div className="phil-error">Unable to load resources. {error}</div>;

  const resources: Resource[] = data?.resources ?? [];

  return (
    <>
      <div className="phil-page-heading">Resources</div>
      <div className="phil-page-sub">
        Program materials, guides, and other documents from the FinTech Innovation Lab team.
      </div>

      {resources.length === 0 ? (
        <div className="phil-card">
          <div style={{ padding: '40px 24px', textAlign: 'center', color: '#888', fontSize: 14 }}>
            No resources are available yet. Check back soon.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {resources.map(r => <ResourceCard key={r.id} resource={r} />)}
        </div>
      )}
    </>
  );
}

function ResourceCard({ resource }: { resource: Resource }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const open = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await API.resourceFileUrl(resource.id);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
      else throw new Error('no-url');
    } catch {
      setErr('Unable to open this file. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="phil-card" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ fontSize: 28 }}>{fileIcon(resource.filename, resource.contentType)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{resource.title}</div>
        {resource.description && (
          <div style={{ fontSize: 13, color: '#666', marginTop: 3 }}>{resource.description}</div>
        )}
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          {resource.filename}{resource.size != null ? ` · ${formatSize(resource.size)}` : ''}
        </div>
        {err && <div className="phil-file-err">{err}</div>}
      </div>
      <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={open} disabled={busy}>
        {busy ? 'Opening…' : 'View / Download'}
      </button>
    </div>
  );
}

function fileIcon(filename: string, contentType: string | null): string {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const ct = contentType || '';
  if (ext === 'pptx' || ext === 'ppt' || ct.includes('presentation')) return '📊';
  if (ext === 'pdf' || ct.includes('pdf')) return '📕';
  if (ext === 'docx' || ext === 'doc' || ct.includes('word')) return '📄';
  if (ext === 'xlsx' || ext === 'xls' || ct.includes('sheet') || ct.includes('excel')) return '📈';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || ct.startsWith('image/')) return '🖼️';
  return '📎';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
